import { Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnInit,
    ViewChild
} from '@angular/core';
import { getFormComponentItemtext } from '../../../shared/utils';

import { SbFieldComponent } from './sb-field-utils';
import { TranslateEnumPipe } from './../../../shared/pipes';

@Component({
    selector: 'app-sb-autocomplete',
    template: `
        <mat-form-field>
            <mat-label>{{ to.label }}</mat-label>
            <input
                #input
                type="text"
                matInput
                autocomplete="do-not-autocomplete"
                [formControl]="formControl"
                [matAutocomplete]="key"
                (focus)="onFocus($event)"
                (blur)="onLeave($event)"
                [required]="to.required"
            />
            <mat-autocomplete #key="matAutocomplete" [displayWith]="displayWithFn">
                <mat-option *ngFor="let option of filteredItems" [value]="option">
                    {{ getElementText(option) | translateEnum }}
                </mat-option>
            </mat-autocomplete>
        </mat-form-field>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SbAutoCompleteComponent
    extends SbFieldComponent
    implements OnInit, AfterViewInit
{
    @ViewChild('input') input: ElementRef;
    private _firstValueSet = false;

    get firstValueSet() {
        return this._firstValueSet;
    }

    set firstValueSet(valueSet: boolean) {
        this._firstValueSet = valueSet;
    }
    constructor(private http: HttpClient, private translate: TranslateEnumPipe) {
        super();
    }

    filteredItems: any[] | Observable<any[]>;

    ngOnInit() {
        this.setInitialValue();

        // Listen to valueChanges in the formControl (the text in the input field) to do custom filtering.
        this.formControl.valueChanges.pipe(debounceTime(500)).subscribe((value) => {
            this.filter(value);
            if (
                !this.firstValueSet &&
                value &&
                typeof value === 'object' &&
                'instrumentId' in value
            ) {
                // Ugly hack to trigger another check of initial value of cashflow instrument set when autofilling forms
                this.setInitialValue();
                this.firstValueSet = true;
            }
        });
    }

    ngAfterViewInit(): void {
        this.input.nativeElement.name = this.getRandomName();
        this.input.nativeElement.setAttribute('data-cy', this.key);
    }

    setInitialValue() {
        if (!this.formControl.value) {
            this.initDefaultValue();
            return;
        }

        // Initial value passed in to component (set in formly data model).
        if (Array.isArray(this.field.templateOptions.options)) {
            this.formControl.setValue(
                (this.field.templateOptions.options as any[]).find((o) =>
                    this.optionMatchesSelectedFormValue(o)
                )
            );
        }
    }

    /**
     * Set default values from templateOptions (For example - default SEK for currency selector) if any.
     */
    private initDefaultValue() {
        if (this.field.templateOptions.defaultValue) {
            this.formControl.setValue(this.field.templateOptions.defaultValue);
        } else if (
            // Set the only available value if no default - and only one option (if configured).
            this.to.selectFirstIfSingle &&
            Array.isArray(this.field.templateOptions.options) &&
            this.field.templateOptions.options.length === 1
        ) {
            this.formControl.setValue(this.field.templateOptions.options[0]);
        }
    }

    optionMatchesSelectedFormValue(o: any): boolean {
        if (this.to.idField) {
            // Stringify hack to not have to compare types (id as string from route vs number in datamodel)
            return (
                '' + o[this.to.idField] === '' + this.formControl.value[this.to.idField]
            );
        } else if (typeof o === 'object' && o.hasOwnProperty('value')) {
            // Old deprecated way of assuming a value field should take what's set in the form control value.
            return '' + o['value'] === '' + this.formControl.value;
        }
        // For primitive types (for example - currency) - or objects with "instrumentId" type
        return (
            o === this.formControl.value ||
            o.instrumentId === this.formControl.value.instrumentId
        );
    }

    displayWithFn = (argument) => {
        return this.translate.transform(this.displayFn(argument, this.to));
    };

    // Mat Autocomplete Display Method
    displayFn(option: any, templateOptions): any {
        if (this.to && this.to.field && option) {
            return option[this.to.field];
        } else if (option && option.label) {
            return option.label;
        } else {
            return option;
        }
    }

    onFocus(event) {
        this.filter('');
    }

    // When this is dirty an empty string is set in the form control - set the value to null to ensure not passing empty string values to server
    onLeave(event) {
        if (this.formControl.value === '') {
            this.formControl.setValue(null);
        }
    }

    getElementText(item) {
        return getFormComponentItemtext(item, this.field.templateOptions.templateFields);
    }

    filter(event) {
        if (this.field.templateOptions.options) {
            this.filteredItems = this.localQuery(
                this.field.templateOptions.options as any[],
                event
            );
        } else if (this.field.templateOptions.optionsURL) {
            this.remoteQuery(this.field.templateOptions.optionsURL, event);
        } else {
            console.error('Invalid arguments provided to fetch remote data.');
        }
    }

    localQuery(options: any[], query: any): any[] {
        let queryOption;
        if (query && query.label) {
            queryOption = query.label;
        } else if (query && query.instrumentName) {
            queryOption = query.instrumentName;
        } else {
            queryOption = query;
        }
        return options.filter((word) => {
            if (typeof word === 'string' && queryOption) {
                return word.toLowerCase().indexOf(queryOption.toLowerCase()) !== -1;
            }
            if (
                word.hasOwnProperty('label') &&
                word.label &&
                typeof queryOption === 'string'
            ) {
                return word.label.toLowerCase().indexOf(queryOption.toLowerCase()) !== -1;
            } else {
                const values = Object.keys(word).map((key) => word[key]);
                return values.find((text) => {
                    return (
                        typeof text === 'string' &&
                        typeof queryOption === 'string' &&
                        text.toLowerCase().indexOf(queryOption.toLowerCase()) !== -1
                    );
                });
            }
        });
    }

    remoteQuery(url: string, query: string) {
        this.http.get<any>(`${url}?q=${encodeURIComponent(query)}`).subscribe((data) => {
            this.filteredItems = data;
        });
    }
}
